import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  BackendPushResponse,
  BackendUpdateResponse,
  Datastore,
  DatastoreCollection,
} from '@freelancer/datastore';
import { Task, TasksCollection } from '@freelancer/datastore/collections';
import { ModalService } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ModalSize } from '@freelancer/ui/modal';
import {
  TaskListApi,
  TaskSourceTypeApi,
  TaskStatusApi,
} from 'api-typings/tasklist/tasklist';
import * as Rx from 'rxjs';
import { createError } from '../task-error/task-error.helper';
import {
  TaskError,
  TaskErrorAction,
  TaskErrorRemedy,
} from '../task-error/task-error.model';
import {
  TaskModalAction,
  TaskModalComponent,
  TaskModalResult,
} from '../task-modal/task-modal.component';

@Component({
  selector: 'app-task-form-change',
  template: `
    <app-task-form
      *ngIf="showForm"
      [currentTask]="currentTask"
      [isBusy]="isBusy$ | flAsync"
      [isDraftTask]="isDraftProject"
      [isNewTask]="isNewTask"
      [isCleanState]="isCleanState"
      [freelancerView]="freelancerView"
      (cleanStateChange)="cleanStateChange.emit($event)"
      (closeForm)="closeFormHandler()"
      (createTask)="createTaskHandler($event)"
      (deleteTask)="deleteTaskHandler($event)"
      (taskEdited)="taskEdited.emit($event)"
      (updateTask)="updateTaskHandler($event)"
    ></app-task-form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./task-form-change.component.scss'],
})
export class TaskFormChangeComponent {
  BannerAlertType = BannerAlertType;
  ModalSize = ModalSize;

  @Input() currentTask?: Partial<Task>;
  @Input() isDraftProject: boolean;
  @Input() isNewTask: boolean;
  @Input() isCleanState: boolean;
  @Input() projectId: number;
  @Input() showForm: boolean;
  @Input() taskListCollection?: DatastoreCollection<TasksCollection>;
  @Input() userId: number;
  @Input() freelancerView: boolean;

  @Output() cleanStateChange = new EventEmitter<boolean>();
  @Output() closeForm = new EventEmitter<void>();
  @Output() error = new EventEmitter<TaskError>();
  @Output() formDirty = new EventEmitter<boolean>();
  @Output() hideError = new EventEmitter<void>();
  @Output() taskEdited = new EventEmitter<Partial<Task>>();

  private _isBusy$ = new Rx.Subject<boolean>();
  isBusy$ = this._isBusy$.asObservable();

  constructor(
    private datastore: Datastore,
    private modalService: ModalService,
  ) {}

  // Called when a task has been requested to be deleted
  deleteTaskHandler(task: Partial<Task>) {
    // Must have an id for the current task to be able to update it, if not id
    // is present it means the task has not been created.
    const taskId = this.currentTask ? this.currentTask.id : undefined;
    if (!taskId) {
      return;
    }
    this.modalService
      .open(TaskModalComponent, {
        inputs: {
          action: TaskModalAction.CONFIRM_DELETE,
          task,
          projectId: this.projectId,
        },
        closeable: false,
        size: ModalSize.SMALL,
      })
      .afterClosed()
      .toPromise()
      .then(response => {
        if (response === TaskModalResult.CONFIRM && this.taskListCollection) {
          this._isBusy$.next(true);
          this.taskListCollection
            .update(taskId, {
              status: TaskStatusApi.CLOSED_DELETED,
            })
            .then(res =>
              this.checkForErrors(
                res,
                TaskErrorAction.TASK_DELETE,
                TaskErrorRemedy.AGAIN,
              ),
            );
        }
      });
  }

  // Called when a task has been modified
  updateTaskHandler(taskFormData: Partial<Task>) {
    // Must have an id for the current task to be able to update it, if not id
    // is present it means the task has not been created.
    const taskId = this.currentTask ? this.currentTask.id : undefined;
    if (!taskId) {
      return;
    }
    if (this.currentTask && this.taskListCollection) {
      this._isBusy$.next(true);
      this.taskListCollection
        .update(taskId, taskFormData)
        .then(res =>
          this.checkForErrors(
            res,
            TaskErrorAction.TASK_SAVE,
            TaskErrorRemedy.AGAIN,
          ),
        );
    }
  }

  // Called when a task has been created
  createTaskHandler(taskFormData: Partial<Task>) {
    this._isBusy$.next(true);
    this.datastore
      .createDocument<TasksCollection>('tasks', {
        id: Date.now(),
        ownerId: this.userId,
        createTime: Date.now(),
        source: {
          id: this.projectId,
          type: TaskSourceTypeApi.PROJECT,
        },
        status: taskFormData.status || TaskStatusApi.IN_PROGRESS,
        list: TaskListApi.DEFAULT_LIST,
        title: taskFormData.title,
        description: taskFormData.description,
        dueDate: taskFormData.dueDate,
      })
      .then(res =>
        this.checkForErrors(
          res,
          TaskErrorAction.TASK_CREATE,
          TaskErrorRemedy.AGAIN,
        ),
      );
  }

  // Handles promise response 'then'.
  checkForErrors(
    res:
      | BackendPushResponse<TasksCollection>
      | BackendUpdateResponse<TasksCollection>,
    action: TaskErrorAction,
    remedy: TaskErrorRemedy,
  ) {
    this._isBusy$.next(false);
    if (res.status === 'success') {
      this.hideError.emit();
      this.closeFormHandler();
    } else {
      this.error.emit(createError(action, remedy, res.errorCode));
    }
  }

  // Called when the form has been closed
  closeFormHandler() {
    this.taskEdited.emit(undefined);
    this.cleanStateChange.emit(true);
    this.formDirty.emit(false);
    this.closeForm.emit();
  }
}
